import React, { useCallback, useMemo } from 'react';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { LESSON_PATHNAME } from '@constants/unacademyIcons/icons.constants';
import Image from '@base/Image';
import { useLessonPlayerStore } from '@stores/lessonPlayerStore';
import { handleControls } from '@utils/unacademyIcons/playerHelper';
import { PLAYER_CONTROLS } from 'types/unacademyIcons/player.types';
import useWindowSize from '@hooks/useWindowSize';
import ControlButton from './ControlButton';
import Settings from './VideoSettings/Settings';

const SecondaryControlsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

type ButtonType = {
  $visible: boolean;
};
const StyledControlButton = styled(ControlButton)<ButtonType>`
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  z-index: ${({ $visible }) => ($visible ? 2 : -1)};
  position: relative;
`;
const SecondaryControls: React.FC = () => {
  const router = useRouter();

  const { player, isFullscreen, courseSlug, nextSlug, isNextComingSoon } =
    useLessonPlayerStore();
  const { isMobile } = useWindowSize();

  const isLessonPlayer = useMemo(
    () => router.pathname === LESSON_PATHNAME,
    [router.pathname]
  );

  const renderFullscreenIcon = useCallback(() => {
    try {
      if (player.isFullscreen()) {
        return 'close-fullscreen.svg';
      }
    } catch {
      return 'open-fullscreen.svg';
    }
    return 'open-fullscreen.svg';
  }, [player]);

  const handleNextView = () => {
    if (player) {
      pushRoute(
        LESSON_PATHNAME,
        `/icons/courses/${courseSlug}/lessons/${nextSlug}`,
        { shallow: true }
      );
    }
  };
  const handleSettings = () => {};

  const renderSize = useCallback(() => {
    if (isMobile || !isFullscreen) return '24px';
    return '32px';
  }, [isMobile, isFullscreen]);

  return (
    <SecondaryControlsWrapper>
      <StyledControlButton
        tooltipTitle="Next Lesson"
        $visible={Boolean(nextSlug) && !isNextComingSoon}
        onClick={handleNextView}
      >
        <Image
          src="next-lesson-icon.svg"
          alt="Next lesson"
          layout="intrinsic"
          width={renderSize()}
          height={renderSize()}
        />
      </StyledControlButton>
      {isLessonPlayer && <Settings />}
      <ControlButton
        tooltipTitle="Fullscreen (f)"
        onClick={() => handleControls(PLAYER_CONTROLS.FULLSCREEN, player)}
      >
        <Image
          src={renderFullscreenIcon()}
          alt="Fullscreen"
          layout="intrinsic"
          width={renderSize()}
          height={renderSize()}
        />
      </ControlButton>
    </SecondaryControlsWrapper>
  );
};

SecondaryControls.displayName = 'Secondary Controls';

export default SecondaryControls;
